/*
  Sidebar navigation drawer (mobile only).

  On desktop (>= 990px) the sidebar is a permanent fixed column and this
  behaviour is inert. Below that it opens/closes as an off-canvas drawer.

  Focus trapping reuses Dawn's global trapFocus/removeTrapFocus from global.js.
*/

const SIDEBAR_DESKTOP_QUERY = '(min-width: 990px)';

class SidebarNav extends HTMLElement {
  constructor() {
    super();

    this.toggle = this.querySelector('.sidebar__toggle');
    this.closeButton = this.querySelector('.sidebar__panel-close');
    this.panel = this.querySelector('.sidebar__panel');
    this.overlay = this.querySelector('.sidebar__overlay');
    this.desktop = window.matchMedia(SIDEBAR_DESKTOP_QUERY);

    this.onToggleClick = this.onToggleClick.bind(this);
    // Wrapped rather than close.bind(this) so the click event is not passed
    // through as close()'s options argument.
    this.onOverlayClick = () => this.close();
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
  }

  connectedCallback() {
    if (this.toggle) this.toggle.addEventListener('click', this.onToggleClick);
    if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);
    if (this.closeButton) this.closeButton.addEventListener('click', this.onOverlayClick);
    this.addEventListener('keyup', this.onKeyUp);

    // Safari < 14 only supports the deprecated addListener.
    if (this.desktop.addEventListener) {
      this.desktop.addEventListener('change', this.onBreakpointChange);
    } else {
      this.desktop.addListener(this.onBreakpointChange);
    }
  }

  disconnectedCallback() {
    if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
    if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);
    if (this.closeButton) this.closeButton.removeEventListener('click', this.onOverlayClick);
    this.removeEventListener('keyup', this.onKeyUp);

    if (this.desktop.removeEventListener) {
      this.desktop.removeEventListener('change', this.onBreakpointChange);
    } else {
      this.desktop.removeListener(this.onBreakpointChange);
    }

    // Never leave the page scroll-locked if the element goes away mid-transition.
    document.body.classList.remove('overflow-hidden');
  }

  get isOpen() {
    return this.dataset.open === 'true';
  }

  onToggleClick() {
    this.isOpen ? this.close() : this.open();
  }

  onKeyUp(event) {
    if (event.code !== 'Escape' || !this.isOpen) return;
    this.close();
  }

  /* Crossing into desktop must clear drawer-only state (scroll lock, focus trap). */
  onBreakpointChange(event) {
    if (event.matches && this.isOpen) this.close({ focusToggle: false });
  }

  open() {
    this.dataset.open = 'true';
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
    if (this.overlay) this.overlay.hidden = false;
    document.body.classList.add('overflow-hidden');

    if (typeof trapFocus === 'function' && this.panel) {
      const firstLink = this.panel.querySelector('.sidebar__link');
      trapFocus(this.panel, firstLink || this.panel);
    }
  }

  close({ focusToggle = true } = {}) {
    this.dataset.open = 'false';
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(focusToggle ? this.toggle : null);
    }

    // Keep the overlay in the DOM until the fade-out finishes.
    if (this.overlay) {
      const hide = () => {
        if (!this.isOpen) this.overlay.hidden = true;
      };
      this.overlay.addEventListener('transitionend', hide, { once: true });
      setTimeout(hide, 350);
    }
  }
}

customElements.define('sidebar-nav', SidebarNav);
